const UGX_MEDIA_GALLERY_DEBOUNCE =
  typeof debounce === 'function'
    ? debounce
    : (fn, wait = 100) => {
        let timeout;
        return (...args) => {
          window.clearTimeout(timeout);
          timeout = window.setTimeout(() => fn.apply(null, args), wait);
        };
      };

const UGX_MEDIA_GALLERY_RAF =
  window.requestAnimationFrame ||
  ((callback) => {
    window.setTimeout(callback, 16);
  });

const ugxClamp = (value, min, max) => Math.max(min, Math.min(max, value));

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        this.ugx = {
          enabled: false,
          bound: false,
          main: null,
          viewer: null,
          track: null,
          slides: [],
          prevButton: null,
          nextButton: null,
          dots: null,
          dotButtons: [],
          index: 0,
          count: 0,
          slideW: 0,
          currentTx: 0,
          clickGuardUntil: 0,
          lastMovedAbs: 0,
          drag: {
            down: false,
            pointerId: null,
            startX: 0,
            startY: 0,
            startTx: 0,
            moved: 0,
            movedAbs: 0,
            locked: false,
            startedAt: 0,
          },
        };

        this.boundUgxResize = UGX_MEDIA_GALLERY_DEBOUNCE(this.onUgxResize.bind(this), 150);
        this.boundUgxPrev = this.onUgxPrev.bind(this);
        this.boundUgxNext = this.onUgxNext.bind(this);
        this.boundUgxDotClick = this.onUgxDotClick.bind(this);
        this.boundUgxPointerDown = this.onUgxPointerDown.bind(this);
        this.boundUgxPointerMove = this.onUgxPointerMove.bind(this);
        this.boundUgxPointerUp = this.onUgxPointerUp.bind(this);
        this.boundUgxClickCapture = this.onUgxClickCapture.bind(this);

        this.initUgxGallery();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            UGX_MEDIA_GALLERY_DEBOUNCE(this.onSlideChanged.bind(this), 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;

            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });

          if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }
      }

      disconnectedCallback() {
        this.destroyUgxGallery();
      }

      initUgxGallery() {
        this.ugx.main = this.querySelector('[data-ugx-product-media-main]');
        this.ugx.viewer = this.querySelector('[data-ugx-gallery-viewer]');
        this.ugx.track = this.querySelector('[data-ugx-gallery-track]');
        this.ugx.prevButton = this.querySelector('[data-ugx-gallery-prev]');
        this.ugx.nextButton = this.querySelector('[data-ugx-gallery-next]');
        this.ugx.dots = this.querySelector('[data-ugx-gallery-dots]');

        this.refreshUgxGallery();

        this.ugx.enabled = Boolean(this.ugx.main && this.ugx.viewer && this.ugx.track && this.ugx.slides.length);

        if (!this.ugx.enabled) return;

        this.bindUgxGallery();

        UGX_MEDIA_GALLERY_RAF(() => {
          this.refreshUgxGallery();

          const activeIndex = this.ugx.slides.findIndex((slide) => slide.classList.contains('is-active'));
          this.ugx.index = activeIndex >= 0 ? activeIndex : 0;

          this.goToUgxIndex(this.ugx.index, {
            animate: false,
            play: false,
            announce: false,
            syncThumbnail: true,
          });
        });
      }

      bindUgxGallery() {
        if (this.ugx.bound || !this.ugx.enabled) return;

        if (this.ugx.prevButton) {
          this.ugx.prevButton.addEventListener('click', this.boundUgxPrev);
        }

        if (this.ugx.nextButton) {
          this.ugx.nextButton.addEventListener('click', this.boundUgxNext);
        }

        if (this.ugx.dots) {
          this.ugx.dots.addEventListener('click', this.boundUgxDotClick);
        }

        if (this.ugx.viewer) {
          this.ugx.viewer.addEventListener('pointerdown', this.boundUgxPointerDown, { passive: true });
          this.ugx.viewer.addEventListener('pointermove', this.boundUgxPointerMove, { passive: false });
          this.ugx.viewer.addEventListener('pointerup', this.boundUgxPointerUp, { passive: true });
          this.ugx.viewer.addEventListener('pointercancel', this.boundUgxPointerUp, { passive: true });
          this.ugx.viewer.addEventListener('click', this.boundUgxClickCapture, true);
        }

        window.addEventListener('resize', this.boundUgxResize, { passive: true });

        this.ugx.bound = true;
      }

      destroyUgxGallery() {
        if (!this.ugx.bound) return;

        if (this.ugx.prevButton) {
          this.ugx.prevButton.removeEventListener('click', this.boundUgxPrev);
        }

        if (this.ugx.nextButton) {
          this.ugx.nextButton.removeEventListener('click', this.boundUgxNext);
        }

        if (this.ugx.dots) {
          this.ugx.dots.removeEventListener('click', this.boundUgxDotClick);
        }

        if (this.ugx.viewer) {
          this.ugx.viewer.removeEventListener('pointerdown', this.boundUgxPointerDown);
          this.ugx.viewer.removeEventListener('pointermove', this.boundUgxPointerMove);
          this.ugx.viewer.removeEventListener('pointerup', this.boundUgxPointerUp);
          this.ugx.viewer.removeEventListener('pointercancel', this.boundUgxPointerUp);
          this.ugx.viewer.removeEventListener('click', this.boundUgxClickCapture, true);
        }

        window.removeEventListener('resize', this.boundUgxResize);
        this.ugx.bound = false;
      }

      refreshUgxGallery() {
        if (!this.ugx.track) return;

        this.ugx.slides = Array.from(this.ugx.track.querySelectorAll('[data-ugx-gallery-slide]'));
        this.ugx.dotButtons = this.ugx.dots
          ? Array.from(this.ugx.dots.querySelectorAll('[data-ugx-gallery-dot]'))
          : [];
        this.ugx.count = this.ugx.slides.length;

        this.ugx.slides.forEach((slide, index) => {
          slide.setAttribute('data-ugx-media-index', String(index));
        });

        this.measureUgxGallery();
        this.ugx.index = ugxClamp(this.ugx.index, 0, Math.max(this.ugx.count - 1, 0));
        this.applyUgxTranslate(this.calcUgxTranslate(this.ugx.index), false);
        this.syncUgxUI();
      }

      measureUgxGallery() {
        if (!this.ugx.viewer) return;

        const viewerRect = this.ugx.viewer.getBoundingClientRect();
        const activeSlide = this.ugx.slides[this.ugx.index] || this.ugx.slides[0];
        const slideRect = activeSlide ? activeSlide.getBoundingClientRect() : null;

        this.ugx.slideW = slideRect && slideRect.width > 0 ? slideRect.width : viewerRect.width || 1;
      }

      calcUgxTranslate(index) {
        return -(this.ugx.slideW * index);
      }

      applyUgxTranslate(tx, animate = true) {
        if (!this.ugx.track) return;

        if (!animate) {
          this.ugx.track.style.transition = 'none';
        } else {
          this.ugx.track.style.transition = '';
        }

        this.ugx.track.style.transform = `translate3d(${tx}px, 0, 0)`;
        this.ugx.currentTx = tx;

        if (!animate) {
          UGX_MEDIA_GALLERY_RAF(() => {
            if (!this.classList.contains('is-ugx-dragging') && this.ugx.track) {
              this.ugx.track.style.transition = '';
            }
          });
        }
      }

      goToUgxIndex(index, options = {}) {
        if (!this.ugx.enabled || !this.ugx.count) return;

        const {
          animate = true,
          play = true,
          announce = true,
          syncThumbnail = true,
        } = options;

        const targetIndex = ugxClamp(index, 0, this.ugx.count - 1);
        this.ugx.index = targetIndex;

        this.ugx.slides.forEach((slide) => {
          slide.classList.remove('is-active');
        });

        const activeMedia = this.ugx.slides[targetIndex];

        if (activeMedia) {
          activeMedia.classList.add('is-active');
        }

        this.measureUgxGallery();
        this.applyUgxTranslate(this.calcUgxTranslate(targetIndex), animate);
        this.syncUgxUI();

        if (activeMedia && play) {
          this.playActiveMedia(activeMedia);
        }

        if (activeMedia && syncThumbnail && this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );

          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);

            if (announce) {
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }
        }
      }

      goToUgxMediaId(mediaId, options = {}) {
        if (!this.ugx.enabled || !mediaId) return false;

        const targetIndex = this.ugx.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);

        if (targetIndex < 0) return false;

        this.goToUgxIndex(targetIndex, options);
        return true;
      }

      syncUgxUI() {
        if (!this.ugx.enabled) return;

        const atStart = this.ugx.index <= 0;
        const atEnd = this.ugx.index >= this.ugx.count - 1;
        const single = this.ugx.count <= 1;

        this.classList.add('is-ugx-ready');
        this.classList.toggle('is-ugx-at-start', atStart || single);
        this.classList.toggle('is-ugx-at-end', atEnd || single);
        this.classList.toggle('is-ugx-single', single);

        if (this.ugx.prevButton) {
          this.ugx.prevButton.disabled = atStart || single;
          this.ugx.prevButton.setAttribute('aria-hidden', atStart || single ? 'true' : 'false');
        }

        if (this.ugx.nextButton) {
          this.ugx.nextButton.disabled = atEnd || single;
          this.ugx.nextButton.setAttribute('aria-hidden', atEnd || single ? 'true' : 'false');
        }

        this.ugx.dotButtons.forEach((dot) => {
          const dotIndex = Number.parseInt(dot.getAttribute('data-index') || '-1', 10);
          const isActive = dotIndex === this.ugx.index;

          dot.classList.toggle('is-active', isActive);

          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      onUgxResize() {
        if (!this.ugx.enabled) return;

        this.refreshUgxGallery();
        this.goToUgxIndex(this.ugx.index, {
          animate: false,
          play: false,
          announce: false,
          syncThumbnail: true,
        });
      }

      onUgxPrev(event) {
        event.preventDefault();
        this.goToUgxIndex(this.ugx.index - 1, { animate: true });
      }

      onUgxNext(event) {
        event.preventDefault();
        this.goToUgxIndex(this.ugx.index + 1, { animate: true });
      }

      onUgxDotClick(event) {
        const dot = event.target.closest('[data-ugx-gallery-dot]');
        if (!dot) return;

        event.preventDefault();

        const index = Number.parseInt(dot.getAttribute('data-index') || '-1', 10);
        if (!Number.isFinite(index) || index < 0) return;

        this.goToUgxIndex(index, { animate: true });
      }

      onUgxPointerDown(event) {
        if (!this.ugx.enabled || this.ugx.count <= 1) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        this.ugx.drag.down = true;
        this.ugx.drag.pointerId = event.pointerId;
        this.ugx.drag.startX = event.clientX;
        this.ugx.drag.startY = event.clientY;
        this.ugx.drag.startTx = this.ugx.currentTx;
        this.ugx.drag.moved = 0;
        this.ugx.drag.movedAbs = 0;
        this.ugx.drag.locked = false;
        this.ugx.drag.startedAt =
          window.performance && performance.now ? performance.now() : Date.now();

        this.classList.add('is-ugx-dragging');

        if (this.ugx.track) {
          this.ugx.track.classList.add('is-ugx-dragging');
          this.ugx.track.style.transition = 'none';
        }

        try {
          this.ugx.viewer.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture is optional.
        }
      }

      onUgxPointerMove(event) {
        if (!this.ugx.drag.down) return;
        if (this.ugx.drag.pointerId !== event.pointerId) return;

        const dx = event.clientX - this.ugx.drag.startX;
        const dy = event.clientY - this.ugx.drag.startY;
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);

        this.ugx.drag.moved = dx;
        this.ugx.drag.movedAbs = adx;
        this.ugx.lastMovedAbs = adx;

        if (!this.ugx.drag.locked) {
          if (ady > adx && ady > 10) {
            this.cancelUgxDrag(event);
            return;
          }

          if (adx > 6) {
            this.ugx.drag.locked = true;
          }
        }

        if (!this.ugx.drag.locked) return;

        if (event.cancelable) {
          event.preventDefault();
        }

        let nextTx = this.ugx.drag.startTx + dx;

        if (this.ugx.index <= 0 && dx > 0) {
          nextTx = this.ugx.drag.startTx + dx * 0.28;
        }

        if (this.ugx.index >= this.ugx.count - 1 && dx < 0) {
          nextTx = this.ugx.drag.startTx + dx * 0.28;
        }

        this.applyUgxTranslate(nextTx, false);
      }

      onUgxPointerUp(event) {
        if (!this.ugx.drag.down) return;
        if (this.ugx.drag.pointerId !== event.pointerId) return;

        const dx = event.clientX - this.ugx.drag.startX;
        const elapsed = Math.max(
          1,
          (window.performance && performance.now ? performance.now() : Date.now()) - this.ugx.drag.startedAt
        );
        const velocity = dx / elapsed;
        const threshold = Math.max(28, this.ugx.slideW * 0.18);

        let delta = 0;

        if (Math.abs(dx) > threshold || Math.abs(velocity) > 0.6) {
          delta = dx < 0 ? 1 : -1;
        }

        if (this.ugx.drag.movedAbs > 8) {
          this.ugx.clickGuardUntil =
            (window.performance && performance.now ? performance.now() : Date.now()) + 260;

          window.setTimeout(() => {
            this.ugx.lastMovedAbs = 0;
          }, 280);
        }

        this.finishUgxDrag(event);

        if (delta !== 0) {
          this.goToUgxIndex(this.ugx.index + delta, { animate: true });
        } else {
          const nearestIndex = Math.round(Math.abs(this.ugx.currentTx) / Math.max(this.ugx.slideW, 1));
          this.goToUgxIndex(nearestIndex, { animate: true });
        }
      }

      cancelUgxDrag(event) {
        this.ugx.drag.down = false;
        this.ugx.drag.locked = false;

        this.classList.remove('is-ugx-dragging');

        if (this.ugx.track) {
          this.ugx.track.classList.remove('is-ugx-dragging');
          this.ugx.track.style.transition = '';
        }

        try {
          this.ugx.viewer.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture release is optional.
        }

        this.goToUgxIndex(this.ugx.index, {
          animate: true,
          play: false,
          announce: false,
          syncThumbnail: false,
        });
      }

      finishUgxDrag(event) {
        this.ugx.drag.down = false;
        this.ugx.drag.locked = false;

        this.classList.remove('is-ugx-dragging');

        if (this.ugx.track) {
          this.ugx.track.classList.remove('is-ugx-dragging');
          this.ugx.track.style.transition = '';
        }

        try {
          this.ugx.viewer.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture release is optional.
        }
      }

      onUgxClickCapture(event) {
        if (!this.ugx.enabled) return;

        const now = window.performance && performance.now ? performance.now() : Date.now();

        if (now < this.ugx.clickGuardUntil || this.ugx.lastMovedAbs > 8) {
          event.preventDefault();
          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail || !event.detail.currentElement) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);

        if (this.ugx.enabled && event.detail.currentElement.dataset.mediaId) {
          this.goToUgxMediaId(event.detail.currentElement.dataset.mediaId, {
            animate: true,
            play: false,
            announce: false,
            syncThumbnail: true,
          });
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');

        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }

          if (this.ugx.enabled) {
            this.refreshUgxGallery();
          }
        }

        this.preventStickyHeader();

        if (this.ugx.enabled) {
          this.goToUgxMediaId(activeMedia.dataset.mediaId, {
            animate: true,
            play: false,
            announce: false,
            syncThumbnail: true,
          });
        } else {
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }

            const activeMediaRect = activeMedia.getBoundingClientRect();

            if (activeMediaRect.top > -0.5) return;

            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        const thumbnailButton = thumbnail.querySelector('button');

        if (thumbnailButton) {
          thumbnailButton.setAttribute('aria-current', true);
        }

        if (
          typeof this.elements.thumbnails.isSlideVisible === 'function' &&
          this.elements.thumbnails.isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !activeItem || !position) return;

        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);

          if (window.accessibilityStrings && window.accessibilityStrings.imageAvailable) {
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          }

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;

        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');

        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');

        if (!this.elements.viewer.sliderItems) return;

        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}